import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Users, History } from 'lucide-react';
import { investorAccountAPI } from '../../services/api';

/**
 * NominationTab — manages the account-level nominee list.
 *
 * Rules (enforced by the backend, mirrored client-side for a friendly UX):
 *  - Multiple nominees per account are supported.
 *  - Total share_percent across ALL nominees on this account must be ≤ 100%.
 *  - Every edit lives in `change_history` (matches Bank & Demat) so ops can
 *    see who changed what and why. History is inline-collapsible per row.
 *
 * Fields per nominee (as requested by product):
 *  - Name of the Nominee (required)
 *  - Address of the Nominee
 *  - Share of Nominee (%)  — number, 0-100
 *  - Date of Birth of Nominee — date
 */
export default function NominationTab({ accountId, canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [totalShare, setTotalShare] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await investorAccountAPI.getNominees(accountId);
      setRows(data.nominees || []);
      setTotalShare(Number(data.total_share_percent || 0));
    } catch { /* no-op */ } finally { setLoading(false); }
  };

  const emptyForm = () => ({ name: '', address: '', share_percent: '', date_of_birth: '', change_remarks: '' });

  const startCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const startEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      address: r.address || '',
      share_percent: r.share_percent ?? '',
      date_of_birth: r.date_of_birth || '',
      change_remarks: '',
    });
    setShowForm(true);
  };

  // Sum of other nominees' shares — used for the live "remaining %" hint.
  const otherShares = editing
    ? rows.filter(r => r.id !== editing.id).reduce((s, r) => s + Number(r.share_percent || 0), 0)
    : totalShare;
  const remaining = Math.max(0, 100 - otherShares);

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('Name of the Nominee is required'); return; }
    const share = form.share_percent === '' || form.share_percent == null ? 0 : Number(form.share_percent);
    if (Number.isNaN(share) || share < 0 || share > 100) {
      alert('Share of Nominee (%) must be a number between 0 and 100'); return;
    }
    if (otherShares + share > 100.000001) {
      alert(`Total nominee share would be ${(otherShares + share).toFixed(2)}% — cannot exceed 100%.`); return;
    }
    if (editing && !form.change_remarks?.trim()) {
      if (!window.confirm('No reason for change was provided. Save anyway?')) return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: (form.address || '').trim(),
        share_percent: share,
        date_of_birth: (form.date_of_birth || '').trim(),
      };
      if (editing) {
        payload.change_remarks = (form.change_remarks || '').trim();
        await investorAccountAPI.updateNominee(accountId, editing.id, payload);
      } else {
        await investorAccountAPI.createNominee(accountId, payload);
      }
      setShowForm(false); setEditing(null); setForm(emptyForm());
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save nominee');
    } finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete nominee "${r.name}"? This can't be undone.`)) return;
    try {
      await investorAccountAPI.deleteNominee(accountId, r.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete nominee');
    }
  };

  const fmtDate = (v) => {
    if (!v) return '--';
    // Accept both YYYY-MM-DD (HTML <input type="date">) and pre-formatted strings.
    try {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { /* noop */ }
    return String(v);
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;

  const isFull = totalShare >= 100 - 0.0000001;

  return (
    <div data-testid="tab-content-nomination">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users size={18} className="text-blue-600" /> Nomination
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Total assigned: <span className={`font-semibold ${isFull ? 'text-green-700' : 'text-blue-700'}`} data-testid="nominee-total-share">{totalShare.toFixed(2)}%</span>
            {' '}/ 100%
            {!isFull && rows.length > 0 && (
              <span className="text-gray-400"> — {(100 - totalShare).toFixed(2)}% remaining</span>
            )}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={startCreate}
            disabled={totalShare >= 100 - 0.0000001}
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-nominee-btn"
            title={isFull ? 'Total share is already 100% — reduce an existing nominee before adding' : undefined}
          >
            <Plus size={14} className="mr-1" /> Add Nominee
          </button>
        )}
      </div>

      {/* Form (create/edit) */}
      {showForm && (
        <div className="mb-4 border border-blue-100 bg-blue-50/40 rounded-lg p-4" data-testid="nominee-form">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">
            {editing ? `Edit Nominee: ${editing.name}` : 'New Nominee'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name of the Nominee <span className="text-red-500">*</span></label>
              <input type="text" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                     data-testid="nominee-name-input" placeholder="Full legal name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Share of Nominee (%) <span className="text-red-500">*</span>
                <span className="ml-1 text-[10px] text-gray-500 font-normal">(max {remaining.toFixed(2)}% remaining)</span>
              </label>
              <input type="number" step="0.01" min="0" max="100"
                     value={form.share_percent ?? ''}
                     onChange={e => setForm(f => ({ ...f, share_percent: e.target.value }))}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                     data-testid="nominee-share-input" placeholder="e.g. 50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date of Birth of Nominee</label>
              <input type="date" value={form.date_of_birth || ''} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                     className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                     data-testid="nominee-dob-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Address of the Nominee</label>
              <textarea rows={2} value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:border-blue-400 focus:outline-none"
                        data-testid="nominee-address-input" placeholder="Full residential address" />
            </div>
            {editing && (
              <div className="sm:col-span-2 border-t border-amber-200 pt-3 mt-1">
                <label className="block text-xs font-medium text-amber-700 mb-1">Reason for change *</label>
                <textarea rows={2} value={form.change_remarks || ''} onChange={e => setForm(f => ({ ...f, change_remarks: e.target.value }))}
                          className="w-full border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-sm resize-none focus:border-amber-400 focus:outline-none"
                          data-testid="nominee-change-remarks-input"
                          placeholder="Briefly describe why these details are being updated (saved in change history)" />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="nominee-cancel-btn">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="nominee-save-btn">
              {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Add Nominee')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {rows.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg" data-testid="nominee-empty">
          No nominees added yet. Investors may appoint one or more nominees whose combined share equals 100%.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`nominee-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-semibold text-gray-900" data-testid={`nominee-name-${r.id}`}>{r.name}</p>
                    <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded" data-testid={`nominee-share-${r.id}`}>
                      {Number(r.share_percent || 0).toFixed(2)}%
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-6 mt-2 text-sm">
                    <div><span className="text-gray-500 text-xs">Date of Birth:</span> <span className="text-gray-900">{fmtDate(r.date_of_birth)}</span></div>
                    <div className="sm:col-span-2"><span className="text-gray-500 text-xs">Address:</span> <span className="text-gray-900 whitespace-pre-wrap">{r.address || '--'}</span></div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {r.change_history?.length > 0 && (
                    <button onClick={() => setExpandedHistory(expandedHistory === r.id ? null : r.id)}
                            className="p-1 text-gray-400 hover:text-gray-700" title="View change history"
                            data-testid={`nominee-history-btn-${r.id}`}>
                      <History size={14} />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => startEdit(r)} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"
                            data-testid={`nominee-edit-btn-${r.id}`}>
                      <Edit size={14} />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(r)} className="p-1 text-gray-400 hover:text-red-500" title="Delete (Admin only)"
                            data-testid={`nominee-delete-btn-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Change History */}
              {expandedHistory === r.id && r.change_history?.length > 0 && (
                <div className="mt-3 border-t pt-2" data-testid={`nominee-history-${r.id}`}>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Change History ({r.change_history.length})</p>
                  <div className="space-y-2">
                    {[...r.change_history].reverse().map((h, i) => {
                      const prev = h.previous || {};
                      const next = h.new || {};
                      const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));
                      const diff = keys
                        .filter(k => String(prev[k] ?? '') !== String(next[k] ?? ''))
                        .map(k => ({ key: k, old: prev[k], new: next[k] }));
                      return (
                        <div key={i} className="bg-gray-50 border rounded p-2">
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">{h.changed_by}</span>
                            <span className="text-gray-400 ml-1">on {new Date(h.changed_at).toLocaleString()}</span>
                          </p>
                          {h.change_remarks && (
                            <p className="text-xs text-amber-700 italic mt-1">Reason: {h.change_remarks}</p>
                          )}
                          {diff.length === 0 ? (
                            <p className="text-[11px] text-gray-400 italic mt-1">No field-level changes detected</p>
                          ) : (
                            <table className="w-full text-[11px] mt-1">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left font-medium pr-2 w-28">Field</th>
                                  <th className="text-left font-medium pr-2">Old Value</th>
                                  <th className="text-left font-medium">New Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {diff.map(d => (
                                  <tr key={d.key} className="align-top">
                                    <td className="pr-2 text-gray-600">{d.key.replace(/_/g, ' ')}</td>
                                    <td className="pr-2 line-through text-red-600 break-all">{String(d.old ?? '—')}</td>
                                    <td className="text-green-700 break-all">{String(d.new ?? '—')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
